import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { LANDING } from '@/constants/testIds';
import { trackPageView } from '@/lib/attribution';
import { ArrowRight, Radar, ShieldCheck, Zap } from 'lucide-react';

const Feature = ({ icon: Icon, title, body }) => (
  <div className="c-card c-card--hover">
    <Icon className="w-5 h-5 text-[#38BDF8] mb-4" strokeWidth={2} />
    <h3 className="text-sm uppercase tracking-[0.2em] text-white mb-2">{title}</h3>
    <p className="text-xs text-gray-500 leading-relaxed">{body}</p>
  </div>
);

export const LandingPage = () => {
  useEffect(() => { trackPageView('landing'); }, []);
  return (
    <div data-testid={LANDING.container} className="max-w-6xl mx-auto px-6 md:px-8 pt-16 pb-24">
      <div className="mb-4 text-[10px] tracking-[0.4em] uppercase text-[#38BDF8]">
        // SYSTEM_ONLINE
      </div>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tighter text-white mb-6 leading-[0.95]">
        Ground-truth intel<br />
        for <span className="text-[#38BDF8]">operators</span>
        <span className="caelix-caret"></span>
      </h1>
      <p className="text-sm sm:text-base text-gray-400 max-w-2xl leading-relaxed mb-10">
        Caelix Intelligence is a signals platform for open-source investigators.
        Streaming OSINT feeds, entity graphs, and burn-down alerts &mdash; all
        wrapped in a terminal you already know how to drive.
      </p>
      <div className="flex flex-wrap items-center gap-4 mb-16">
        <Link
          to="/pricing"
          data-testid={LANDING.goPricing}
          className="c-btn-primary inline-flex items-center gap-2 text-sm"
        >
          UPGRADE_CLEARANCE <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
        </Link>
        <Link to="/signup" data-testid={LANDING.goSignup} className="c-btn-secondary text-sm">
          CREATE_OPERATOR
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        <Feature
          icon={Radar}
          title="LIVE_SIGNALS"
          body="Real-time streams from 200+ sources, deduped and entity-normalized before they hit your queue."
        />
        <Feature
          icon={ShieldCheck}
          title="TAMPER_EVIDENT"
          body="Every intel packet is content-hashed. Chain-of-custody is baked in, not bolted on."
        />
        <Feature
          icon={Zap}
          title="INSTANT_TRIAGE"
          body="Rules engine + saved queries fire before your coffee lands. Zero polling required."
        />
      </div>

      <div className="mt-16 border border-[#3F3F46] p-6 bg-[#18181B]">
        <div className="text-[10px] tracking-[0.3em] uppercase text-gray-500 mb-2">
          $ caelix --status
        </div>
        <pre className="text-xs text-gray-400 whitespace-pre-wrap leading-relaxed">
{`> operators.active     : 4,193
> signals.processed/24h: 2.71M
> incidents.confirmed  : 128
> uptime               : 99.998%
> plan.pro             : $19/mo · $190/yr  (2 months free)
> referral.reward      : +1 month for you AND them, on first paid upgrade`}
        </pre>
      </div>
    </div>
  );
};

export default LandingPage;
